import { Box, Button, FormField, Modal, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../store/api.context';
import { useChallenges } from '../../../store/challenge.context';
import { useEvents } from '../../../store/events.context';
import { ChallengeDescriptor } from '../../../types/Challenge';
import { Event } from '../../../types/Event';
import { Nullable } from '../../../types/common';
import { i18nKeys } from '../../../utils/i18n.utils';

interface ReplaceChallengeWithBackupModalProps {
  visible: boolean;
  onDismiss: () => void;
  target: Event;
  challengeIdToReplace: string;
  challengeTitle: string;
}

/**
 * Replaces an active event challenge with a backup, grouped into "Backups for this Challenge"
 * (per-challenge backups) and "Backups for this Event" (general backups).
 */
const ReplaceChallengeWithBackupModal: React.FC<ReplaceChallengeWithBackupModalProps> = ({
  visible,
  onDismiss,
  target,
  challengeIdToReplace,
  challengeTitle,
}) => {
  const { t } = useTranslation();
  const { eventsApi } = useApi();
  const { getEventByName } = useEvents();
  const { challengeTitles } = useChallenges();
  const [selectedOption, setSelectedOption] = useState<Nullable<SelectProps.Option>>(null);
  const [loading, setLoading] = useState(false);

  const backupOptionLabel = (backup: ChallengeDescriptor): string => {
    const id = backup.challengeId || '';
    const title = challengeTitles[id];
    return title ? `${title} (${id})` : id;
  };

  const toOption = (backup: ChallengeDescriptor): SelectProps.Option => ({
    label: backupOptionLabel(backup),
    value: backup.challengeId || '',
  });

  const optionGroups = useMemo<SelectProps.OptionGroup[]>(() => {
    const config = target.backupChallengeConfig;
    if (!config) {
      return [];
    }

    const perChallengeForSelected = config.perChallengeBackups?.[challengeIdToReplace] ?? [];

    const selectedChallengeIds = new Set(
      target.challengeDescriptors.map((cd) => cd.challengeId).filter((id): id is string => !!id)
    );

    // The event group is general backups only, minus any that are a per-challenge backup of an
    // unselected challenge.
    const perChallengeBackupIdsOfUnselected = new Set<string>();
    Object.entries(config.perChallengeBackups ?? {}).forEach(([ownerChallengeId, backups]) => {
      if (!selectedChallengeIds.has(ownerChallengeId)) {
        (backups ?? []).forEach((b) => {
          if (b.challengeId) {
            perChallengeBackupIdsOfUnselected.add(b.challengeId);
          }
        });
      }
    });

    const eventBackups: ChallengeDescriptor[] = (config.generalBackups ?? []).filter(
      (b) => !b.challengeId || !perChallengeBackupIdsOfUnselected.has(b.challengeId)
    );

    const groups: SelectProps.OptionGroup[] = [];

    const seen = new Set<string>();
    // Drop duplicates, the challenge being replaced, and any challenge already active in the event.
    const dedupe = (backups: ChallengeDescriptor[]): ChallengeDescriptor[] =>
      backups.filter((b) => {
        const id = b.challengeId;
        if (!id || seen.has(id) || id === challengeIdToReplace || selectedChallengeIds.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      });

    const perChallengeOptions = dedupe(perChallengeForSelected);
    if (perChallengeOptions.length > 0) {
      groups.push({
        label: t(i18nKeys.challenges.backupChallenges.replaceWithBackup.perChallengeGroupLabel),
        options: perChallengeOptions.map(toOption),
      });
    }

    const eventOptions = dedupe(eventBackups);
    if (eventOptions.length > 0) {
      groups.push({
        label: t(i18nKeys.challenges.backupChallenges.replaceWithBackup.eventGroupLabel),
        options: eventOptions.map(toOption),
      });
    }

    return groups;
    // challengeTitles dep refreshes labels once titles load
  }, [target, challengeIdToReplace, challengeTitles]);

  const handleDismiss = () => {
    setSelectedOption(null);
    onDismiss();
  };

  const handleConfirm = async () => {
    const backupChallengeId = selectedOption?.value;
    if (!backupChallengeId) {
      return;
    }
    setLoading(true);
    try {
      await eventsApi.replaceEventChallenge(target.name, challengeIdToReplace, backupChallengeId);
      await getEventByName(target.name);
      handleDismiss();
    } catch {
      // API client shows a failure flashbar; keep the modal open to retry.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={t(i18nKeys.challenges.backupChallenges.replaceWithBackup.modalTitle)}
      data-testid="replace-challenge-with-backup-modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xxs">
            <Button variant="link" onClick={handleDismiss} data-testid="replace-challenge-cancel-btn">
              {t(i18nKeys.challenges.backupChallenges.replaceWithBackup.cancel)}
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleConfirm()}
              disabled={!selectedOption}
              loading={loading}
              data-testid="replace-challenge-confirm-btn"
            >
              {t(i18nKeys.challenges.backupChallenges.replaceWithBackup.confirm)}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>{t(i18nKeys.challenges.backupChallenges.replaceWithBackup.description)}</Box>
        <FormField label={t(i18nKeys.challenges.backupChallenges.replaceWithBackup.challengeToReplaceLabel)}>
          <SpaceBetween size="xxs">
            <Box variant="strong" data-testid="replace-challenge-title">
              {challengeTitle}
            </Box>
            <Box variant="small" data-testid="replace-challenge-id">
              {t(i18nKeys.challenges.backupChallenges.replaceWithBackup.challengeId, {
                challengeId: challengeIdToReplace,
              })}
            </Box>
          </SpaceBetween>
        </FormField>
        <FormField label={t(i18nKeys.challenges.backupChallenges.replaceWithBackup.selectLabel)}>
          <Select
            selectedOption={selectedOption}
            onChange={({ detail }) => setSelectedOption(detail.selectedOption)}
            options={optionGroups}
            placeholder={t(i18nKeys.challenges.backupChallenges.replaceWithBackup.selectPlaceholder)}
            data-testid="replace-challenge-backup-select"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default ReplaceChallengeWithBackupModal;
